import { useEffect, useState } from "react";
import { Accordion, AccordionPanel, Box, Button, Layer, Text } from "grommet";
import { buildHistoryEntries } from "./podSessionHistory";

function isCompactValue(value) {
  const text = String(value ?? "");
  return text.length <= 80 && !text.includes("\n");
}

function HistoryChange({ change }) {
  const compact =
    isCompactValue(change.value) &&
    (change.previous === undefined || isCompactValue(change.previous));
  const valueText =
    change.previous === undefined
      ? change.value
      : `${change.previous} → ${change.value}`;

  if (compact) {
    return (
      <Text size="small">
        <Text size="small" weight="bold">
          {change.label}:{" "}
        </Text>
        {valueText}
      </Text>
    );
  }

  return (
    <Box gap="xxsmall">
      <Text size="small" weight="bold">
        {change.label}
      </Text>
      {change.previous !== undefined && (
        <Text
          size="small"
          color="dark-4"
          style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
        >
          {change.previous}
        </Text>
      )}
      <Text
        size="small"
        style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      >
        {change.previous === undefined ? change.value : `→ ${change.value}`}
      </Text>
    </Box>
  );
}

export default function PodSessionHistoryModal({ onClose, loadHistory }) {
  const [entries, setEntries] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    loadHistory()
      .then((result) => {
        if (cancelled) return;
        setEntries(buildHistoryEntries(result?.items ?? []));
        setTruncated(Boolean(result?.truncated));
        setLoading(false);
      })
      .catch((loadError) => {
        console.error(loadError);
        if (cancelled) return;
        setError("Unable to load save history.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadHistory]);

  let body = null;

  if (loading) {
    body = <Text>Loading save history...</Text>;
  } else if (error) {
    body = (
      <Text color="status-critical" role="alert">
        {error}
      </Text>
    );
  } else if (entries.length === 0) {
    body = <Text>No saves have been recorded for this session yet.</Text>;
  } else {
    body = (
      <Accordion multiple>
        {entries.map((entry) => (
          <AccordionPanel
            key={entry.key}
            label={
              <Box pad={{ vertical: "xsmall" }} gap="xxsmall">
                <Text weight="bold">{entry.timeLabel}</Text>
                <Text size="small" color="dark-4">
                  {entry.actor} · {entry.summary}
                </Text>
              </Box>
            }
          >
            <Box pad={{ bottom: "small", horizontal: "small" }} gap="xsmall">
              {entry.notesBaseline && (
                <Text size="small" color="dark-4">
                  {truncated
                    ? "Older saves are not loaded, so this lists the notes stored in this save."
                    : "Earliest notes save. Later saves list only what changed."}
                </Text>
              )}
              {entry.changes.length === 0 ? (
                <Text size="small">No field values changed in this save.</Text>
              ) : (
                entry.changes.map((change) => (
                  <HistoryChange key={change.key} change={change} />
                ))
              )}
            </Box>
          </AccordionPanel>
        ))}
      </Accordion>
    );
  }

  return (
    <Layer
      position="center"
      modal
      onEsc={onClose}
      onClickOutside={onClose}
    >
      <Box
        width={{ width: "large", max: "92vw" }}
        pad="medium"
        gap="medium"
        style={{ maxHeight: "85vh", overflow: "auto" }}
      >
        <Box direction="row" justify="between" align="center" gap="small">
          <Box gap="xxsmall">
            <Text weight="bold" size="large">
              Save history
            </Text>
            {!loading && !error && (
              <Text size="small" color="dark-4">
                {entries.length === 1
                  ? "1 save"
                  : `${entries.length} saves`}
              </Text>
            )}
          </Box>
          <Button label="Close" onClick={onClose} />
        </Box>

        {truncated && !loading && !error && (
          <Text size="small">
            Some older saves were not loaded.
          </Text>
        )}

        {body}
      </Box>
    </Layer>
  );
}
